import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Switch,
  Upload,
  Button,
  Image,
  message,
  Row,
  Col,
  Typography,
  Select,
} from 'antd';
import {
  UploadOutlined,
  PictureOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { MiniQuizQuestion, MiniQuizFormData } from '@/types';
import { miniQuizService } from '@/services/miniQuizService';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

interface QuestionFormModalProps {
  visible: boolean;
  editingQuestion?: MiniQuizQuestion | null;
  onSubmit: (values: MiniQuizFormData) => void;
  onCancel: () => void;
}

const QuestionFormModal: React.FC<QuestionFormModalProps> = ({
  visible,
  editingQuestion,
  onSubmit,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState<string>(editingQuestion?.imageUrl || '');
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await miniQuizService.getCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Failed to load categories:', error);
        // Fallback to hardcoded categories
        setCategories(['30nam', 'megasale', 'myshop', 'tindung', 'xdigi']);
      }
    };

    if (visible) {
      loadCategories();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && editingQuestion) {
      form.setFieldsValue({
        question: editingQuestion.question,
        correctAnswer: editingQuestion.correctAnswer,
        explanation: editingQuestion.explanation || '',
        isActive: editingQuestion.isActive,
        category: editingQuestion.category || undefined,
      });
      setImageUrl(editingQuestion.imageUrl);
    } else if (visible) {
      form.resetFields();
      setImageUrl('');
    }
  }, [visible, editingQuestion, form]);

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    try {
      const response = await miniQuizService.uploadImage(file);
      setImageUrl(response.url);
      form.setFieldsValue({ imageUrl: response.url });
      message.success('Image uploaded successfully');
    } catch (error) {
      message.error('Failed to upload image');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImageUrl('');
    form.setFieldsValue({ imageUrl: '' });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!imageUrl) {
        message.error('Please upload an image for the question');
        return;
      }

      const formData: MiniQuizFormData = {
        ...values,
        imageUrl,
      };

      await onSubmit(formData);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const title = editingQuestion ? 'Edit Question' : 'Add New Question';

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      width={800}
      confirmLoading={uploading}
      okText={editingQuestion ? 'Update' : 'Create'}
      cancelText="Cancel"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          correctAnswer: true,
          isActive: true,
        }}
      >
        {/* Image Upload */}
        <Form.Item
          label="Question Image"
          required
        >
          <div style={{ marginBottom: 16 }}>
            {imageUrl ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <Image
                  crossOrigin='anonymous'
                  src={imageUrl}
                  alt="Question"
                  width={200}
                  height={150}
                  style={{ objectFit: 'cover', borderRadius: 8 }}
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleRemoveImage}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'rgba(255, 255, 255, 0.8)',
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  width: 200,
                  height: 150,
                  border: '2px dashed #d9d9d9',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  background: '#fafafa',
                }}
              >
                <PictureOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                <Text type="secondary">No image selected</Text>
              </div>
            )}
          </div>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => {
              handleImageUpload(file);
              return false;
            }}
          >
            <Button 
              icon={<UploadOutlined />} 
              loading={uploading}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
          </Upload>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Supported formats: JPG, PNG, GIF, WebP (Max: 5MB)
            </Text>
          </div>
        </Form.Item>

        {/* Question Text */}
        <Form.Item
          name="question"
          label="Question Text"
          rules={[
            { required: true, message: 'Please enter the question text' },
            { min: 10, message: 'Question must be at least 10 characters' },
            { max: 500, message: 'Question must not exceed 500 characters' },
          ]}
        >
          <TextArea
            placeholder="Enter the question text..."
            rows={4}
            showCount
            maxLength={500}
          />
        </Form.Item>

        {/* Category */}
        <Form.Item
          name="category"
          label="Category (Optional)"
          help="Select a category for this question"
        >
          <Select
            placeholder="Select a category..."
            allowClear
            style={{ width: '100%' }}
          >
            {categories.map((category) => (
              <Option key={category} value={category}>
                {category}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Explanation */}
        <Form.Item
          name="explanation"
          label="Explanation (Optional)"
          rules={[
            { max: 1000, message: 'Explanation must not exceed 1000 characters' },
          ]}
        >
          <TextArea
            placeholder="Enter an explanation for the answer (optional)..."
            rows={3}
            showCount
            maxLength={1000}
          />
        </Form.Item>

        {/* Correct Answer */}
        <Form.Item
          name="correctAnswer"
          label="Correct Answer"
          help="Select True or False as the correct answer for this question"
          rules={[{ required: true, message: 'Please select the correct answer' }]}
        >
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
            prevValues.correctAnswer !== currentValues.correctAnswer
          }>
            {({ getFieldValue }) => {
              const correctAnswer = getFieldValue('correctAnswer');
              return (
                <Row gutter={16}>
                  <Col span={12}>
                    <Button
                      type={correctAnswer === true ? 'primary' : 'default'}
                      block
                      onClick={() => form.setFieldsValue({ correctAnswer: true })}
                      style={{ height: 48 }}
                    >
                      TRUE
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      type={correctAnswer === false ? 'primary' : 'default'}
                      block
                      onClick={() => form.setFieldsValue({ correctAnswer: false })}
                      style={{ height: 48 }}
                    >
                      FALSE
                    </Button>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
        </Form.Item>

        {/* Status */}
        <Form.Item
          name="isActive"
          label="Status"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default QuestionFormModal;
